/**
 * iter63 — central SEO helper for Sri Durga Foods.
 *
 * • Builds the canonical map between the human-friendly category labels
 *   used in the DB (`"Wood Pressed Oils"`) and the URL path segments
 *   (`"wood-pressed-oils"`).  KEEP IN SYNC with /app/backend/seo_slugs.py.
 *
 * • Generates the JSON-LD Product schema fed into react-helmet-async on
 *   every PDP — Google validates this with its Rich Results test.
 *
 * • Exports `productUrl()` and `categoryUrl()` so the rest of the app
 *   navigates exclusively to SEO-friendly URLs.
 */

export const SITE_ORIGIN = "https://sridurgafoods.com";

export const CATEGORY_TO_PATH = {
  "Pickles": "pickles",
  "Culinary Pastes": "pastes",
  "Wood Pressed Oils": "wood-pressed-oils",
  "Traditional Powders": "podis",
  "Masalas": "masala-powders",
  "Spices": "spices",
  "Flours": "flours",
  "Combos": "combos",
};

export const PATH_TO_CATEGORY = Object.fromEntries(
  Object.entries(CATEGORY_TO_PATH).map(([k, v]) => [v, k]),
);

/** Lightweight slugifier used as a fallback when the product hasn't been
 *  bound to an SEO entry yet.  Mirrors /app/backend/seo_slugs.py::_slugify. */
const slugify = (name) =>
  String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

/** iter79 — Return the URL path segment for a DB category. Honours the
 *  curated CATEGORY_TO_PATH map for well-known categories (SEO-optimised
 *  slugs like ``wood-pressed-oils``) and falls back to a slugified version
 *  of the raw category name for admin-created categories.  Mirrors
 *  /app/backend/seo_slugs.py::category_path_for so URLs stay in sync. */
export const categoryPathFor = (category) =>
  CATEGORY_TO_PATH[category] || slugify(category) || "shop";

/** Return the canonical category landing URL ("/pickles", "/spices", or
 *  a dynamic "/whole-spices" for admin-created categories). */
export const categoryUrl = (category) => `/${categoryPathFor(category)}`;

/** Return the canonical product URL.  If the product carries a backend
 *  `seo.url_path`, use it as-is; otherwise derive from the product's
 *  actual DB category (iter79 — never falls back to a hardcoded
 *  category, so admin-created categories work without a redeploy). */
export const productUrl = (product) => {
  if (!product) return "/shop";
  if (product.seo?.url_path) return product.seo.url_path;
  const cat = categoryPathFor(product.category);
  return `/${cat}/${slugify(product.name)}`;
};

/** Trim text safely to a max length without slicing mid-word when possible. */
const clamp = (s, max) => {
  if (!s) return "";
  const t = String(s).trim();
  if (t.length <= max) return t;
  const slice = t.slice(0, max - 1);
  const lastSpace = slice.lastIndexOf(" ");
  return (lastSpace > 40 ? slice.slice(0, lastSpace) : slice) + "…";
};

/** Build the JSON-LD object for a single product. */
export const buildProductJsonLd = ({ product, seo, selectedVariant, absoluteUrl, absoluteImage }) => {
  // Strip any HTML from the description before emitting it in JSON-LD.
  // Google specifically requests plain text — HTML in description is
  // one of the top reasons Rich Results Test flags a Product entity.
  const stripHtml = (s) => String(s || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const offers = (product.variants || []).map((v) => ({
    "@type": "Offer",
    "url": absoluteUrl,
    "priceCurrency": "INR",
    "price": Number(v.price).toFixed(2),
    "name": v.label,
    "availability": v.available !== false
      ? "https://schema.org/InStock"
      : "https://schema.org/OutOfStock",
    "itemCondition": "https://schema.org/NewCondition",
    "seller": { "@type": "Organization", "name": "Sri Durga Foods" },
  }));
  // If there are no variants fall back to a single Offer using product.price.
  const offerNode = offers.length > 0
    ? (offers.length === 1
        ? offers[0]
        : {
            "@type": "AggregateOffer",
            "priceCurrency": "INR",
            "lowPrice": Number(Math.min(...offers.map((o) => Number(o.price)))).toFixed(2),
            "highPrice": Number(Math.max(...offers.map((o) => Number(o.price)))).toFixed(2),
            "offerCount": offers.length,
            "availability": product.in_stock !== false
              ? "https://schema.org/InStock"
              : "https://schema.org/OutOfStock",
            "offers": offers,
          })
    : {
        "@type": "Offer",
        "url": absoluteUrl,
        "priceCurrency": "INR",
        "price": Number(product.price || 0).toFixed(2),
        "availability": product.in_stock !== false
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
        "itemCondition": "https://schema.org/NewCondition",
        "seller": { "@type": "Organization", "name": "Sri Durga Foods" },
      };

  // iter82 — every product image in one Product.image array so Google
  // picks the best-fit hero.  Deduped and absolutised by the caller.
  const gallery = Array.from(new Set([absoluteImage, ...(product.images || []).map((u) => u || "")]))
    .filter(Boolean);

  const node = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": product.name,
    "description": stripHtml(product.seo_description || seo?.meta_description || product.description || ""),
    "image": gallery.length > 1 ? gallery : (gallery[0] || absoluteImage),
    "sku": product.product_id,
    "brand": { "@type": "Brand", "name": "Sri Durga Foods" },
    "category": product.category,
    "url": absoluteUrl,
    "offers": offerNode,
  };
  // iter82 — attach GTIN / MPN when the admin has supplied them. Google
  // Shopping strongly prefers Products carrying at least one identifier;
  // otherwise Rich Results Test emits a "no identifier" warning.
  const gtin = (product.gtin || "").trim();
  const mpn = (product.mpn || "").trim();
  if (gtin) node.gtin = gtin;
  if (mpn) node.mpn = mpn;
  return node;
};

/** Build a BreadcrumbList JSON-LD for the breadcrumb shown on PDPs. */
export const buildBreadcrumbJsonLd = (crumbs) => ({
  "@context": "https://schema.org/",
  "@type": "BreadcrumbList",
  "itemListElement": crumbs.map((c, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "name": c.name,
    "item": `${SITE_ORIGIN}${c.path}`,
  })),
});

export const truncate = clamp;

/** Common Organization JSON-LD shown on the home page footer. */
export const ORGANIZATION_JSON_LD = {
  "@context": "https://schema.org/",
  "@type": "Organization",
  "name": "Sri Durga Foods",
  "alternateName": "Sri Durga",
  "url": SITE_ORIGIN,
  "logo": `${SITE_ORIGIN}/logo.png`,
  "description":
    "Authentic Andhra pickles, culinary pastes, spices, masalas, traditional powders, flours and wood-pressed oils — handcrafted in Nellore, Andhra Pradesh.",
  "contactPoint": [
    {
      "@type": "ContactPoint",
      "telephone": "+91-93916-88723",
      "contactType": "customer service",
      "areaServed": "IN",
      "availableLanguage": ["en", "te", "hi"],
    },
  ],
  "sameAs": [],
};
